/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Sign In Page - Velocity PPM Enterprise Style
 * 
 * Provides email/password authentication and social login options
 * (Google, Microsoft) based on platform configuration.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@shared/contexts/AuthContext';
import { useWorkOSAvailability } from '@shared/components/auth/WorkOSProvider';
import { useWorkOSAuth } from '@shared/hooks/useWorkOSAuth';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react';
import type { LoginRequestData } from '@shared/types/requests/auth';
import type { SocialProvider as SocialProviderType } from '@shared/types/auth';
import type { AuthProvidersResponseData } from '@shared/types/responses/config';
import { createQueryOptions } from '@shared/lib/api';
import { useQuery } from '@tanstack/react-query';
import { getProviderLogo } from '@shared/components/auth';

/**
 * Inner component that handles WorkOS login button.
 * This component is ONLY rendered when WorkOS is available (isWorkOSAvailable is true),
 * which means the AuthKitProvider context exists and useWorkOSAuth can be safely called.
 */
function WorkOSLoginButtonInner({ 
  name,
  onSuccess, 
  onError, 
  onLoadingChange,
  disabled 
}: { 
  name: string;
  onSuccess: () => void;
  onError: (error: Error) => void;
  onLoadingChange: (loading: boolean) => void;
  disabled: boolean;
}) {
  const { signInWithWorkOS, isLoading } = useWorkOSAuth();

  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  const handleClick = async () => {
    try {
      await signInWithWorkOS();
      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err : new Error('WorkOS login failed'));
    }
  };

  return (
    <button
      className="btn"
      style={{
        width: '100%',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        background: 'white',
        color: '#333',
        border: '1px solid #ddd'
      }}
      onClick={handleClick}
      disabled={disabled || isLoading}
    >
      {getProviderLogo('workos')}
      <span>Continue with {name}</span>
    </button>
  );
}

interface SocialProvider {
  id: SocialProviderType;
  name: string;
  icon: React.ReactNode;
  available: boolean;
}

export default function SignInPage() {
  const navigate = useNavigate();
  const { login, socialLogin, isLoading, isAuthenticated } = useAuth();
  const { isWorkOSAvailable } = useWorkOSAvailability();
  const { toast } = useToast();
  const [workosLoading, setWorkosLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState<LoginRequestData>({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Get available authentication providers from config API
  const { data: authProviders, isLoading: providersLoading } = useQuery(
    createQueryOptions<AuthProvidersResponseData>('/api/config/auth/providers')
  );

  // Convert API providers to UI providers with icons
  const socialProviders: SocialProvider[] = React.useMemo(() => {
    if (!authProviders) return [];

    return authProviders
      .filter(provider => provider.id === 'google' || provider.id === 'microsoft' || provider.id === 'workos')
      .map(provider => ({
        id: provider.id as SocialProviderType,
        name: provider.name,
        available: provider.enabled,
        icon: getProviderLogo(provider.id)
      }));
  }, [authProviders]);

  const handleInputChange = (field: keyof LoginRequestData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      await login(formData);
      toast({
        title: 'Welcome back!',
        description: 'You have successfully signed in.',
      });
      navigate('/');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      setError(errorMessage);
      toast({
        title: 'Sign in failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleSocialLogin = async (provider: SocialProviderType) => {
    // WorkOS is handled by the WorkOSLoginButtonInner component
    if (provider === 'workos') {
      return;
    }
    
    setError(null);

    try {
      await socialLogin(provider);
      toast({
        title: 'Welcome back!',
        description: 'You have successfully signed in.',
      });
      navigate('/');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      setError(errorMessage);
      toast({
        title: 'Sign in failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleWorkOSSuccess = () => {
    toast({
      title: 'Welcome back!',
      description: 'You have successfully signed in.',
    });
    navigate('/');
  };

  const handleWorkOSError = (error: Error) => {
    setError(error.message);
    setWorkosLoading(false);
    toast({
      title: 'Sign in failed',
      description: error.message,
      variant: 'destructive',
    });
  };

  const availableProviders = socialProviders.filter(p => p.available);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '440px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '32px 32px 24px',
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#0066cc',
            marginBottom: '8px'
          }}>
            Velocity PPM
          </div>
          <div style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '4px'
          }}>
            Product & Project Management
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#333',
            marginTop: '16px'
          }}>
            Sign in to your account
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '32px' }}>
          {/* Social Login Buttons */}
          {(providersLoading || availableProviders.length > 0) && (
            <div style={{ marginBottom: '24px' }}>
              {providersLoading && (
                <button 
                  className="btn"
                  style={{ 
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    background: '#f0f0f0',
                    color: '#666'
                  }}
                  disabled
                >
                  <Loader2 size={18} className="spin" />
                  Checking authentication providers...
                </button>
              )}
              {availableProviders.map((provider) => (
                provider.id === 'workos' && isWorkOSAvailable ? (
                  <WorkOSLoginButtonInner
                    key={provider.id}
                    name={provider.name}
                    onSuccess={handleWorkOSSuccess}
                    onError={handleWorkOSError}
                    onLoadingChange={setWorkosLoading}
                    disabled={isLoading || providersLoading || workosLoading}
                  />
                ) : provider.id !== 'workos' ? (
                  <button
                    key={provider.id}
                    className="btn"
                    style={{
                      width: '100%',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      background: 'white',
                      color: '#333',
                      border: '1px solid #ddd'
                    }}
                    onClick={() => handleSocialLogin(provider.id)}
                    disabled={isLoading || providersLoading || workosLoading}
                  >
                    {provider.icon}
                    <span>Continue with {provider.name}</span>
                  </button>
                ) : null
              ))}
              
              {availableProviders.length > 0 && (
                <div style={{
                  position: 'relative',
                  margin: '24px 0',
                  textAlign: 'center'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: '1px',
                    backgroundColor: '#e0e0e0'
                  }} />
                  <span style={{
                    position: 'relative',
                    backgroundColor: 'white',
                    padding: '0 12px',
                    fontSize: '12px',
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Or continue with email
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                color: '#c33',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#333',
                fontSize: '14px'
              }}>
                Email
              </label>
              <div style={{ position: 'relative' }}>
                <Mail style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999'
                }} size={18} />
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 42px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password" style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#333',
                fontSize: '14px'
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999'
                }} size={18} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 42px 12px 42px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#999'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              className="btn"
              style={{
                width: '100%',
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 32px',
          textAlign: 'center',
          backgroundColor: '#fafafa',
          borderTop: '1px solid #f0f0f0'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Don't have an account?{' '}
            <Link 
              to="/signup"
              style={{
                color: '#0066cc',
                textDecoration: 'none',
                fontWeight: '600'
              }}
              onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

